import React, { useState, useRef, useEffect } from 'react';
import { read, utils } from 'xlsx';
import './UploadSpreadsheet.css';
import LoadingLogo from './LoadingLogo';
import Tooltip from './Tooltip';

interface ParsedEntry {
    plateNumber: string;
    name: string;
    email: string;
    phone: string | null;
    matchType?: 'exact' | 'plate-only' | 'email-only' | 'new' | 'email-change' | 'phone-change' | 'name-change' | 'phone-add';
    changes?: {
        name?: 'new' | 'changed' | 'unchanged';
        email?: 'new' | 'changed' | 'unchanged';
        phone?: 'new' | 'changed' | 'unchanged';
    };
}

interface ExistingEntry {
    plateNumber: string;
    name: string;
    email: string;
    phone: string | null;
}

interface UploadSpreadsheetProps {
    isOpen: boolean;
    onClose: () => void;
    onAddToRegistry: (entries: ParsedEntry[]) => void;
    existingEntries: ExistingEntry[];
}

const UploadSpreadsheet: React.FC<UploadSpreadsheetProps> = ({ isOpen, onClose, onAddToRegistry, existingEntries }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [parsedEntries, setParsedEntries] = useState<ParsedEntry[]>([]);
    const [errors, setErrors] = useState<string[]>([]);
    const [infoMessages, setInfoMessages] = useState<string[]>([]);
    const [showConfirmDiscard, setShowConfirmDiscard] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [showExactMatches, setShowExactMatches] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Add keyboard event handlers
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                if (showConfirmDiscard) {
                    setShowConfirmDiscard(false);
                } else {
                    onClose();
                }
            } else if (e.key === "Enter" && !isLoading) {
                // Primary action depends on current view
                if (parsedEntries.length > 0 && errors.length === 0) {
                    handleAddToRegistry();
                } else if (errors.length > 0) {
                    // When in error state, "Try Again" is primary
                    setErrors([]);
                    setParsedEntries([]);
                    fileInputRef.current?.click();
                }
                // No action in upload view (need to select file first)
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, parsedEntries, errors, isLoading, showConfirmDiscard, onClose]);

    const loadingMessages = [
        'Parsing Sheet...',
        'Counting Vehicles...',
        'Validating Data...',
        'Checking Format...',
        'Almost Done...'
    ];

    // Add possible header names for each column
    const possiblePlateHeaders = ['plate', 'license', 'vehicle', 'id', 'user', 'parker', 'guest', 'registration', 'tag', 'number'];
    const possibleNameHeaders = ['name', 'owner', 'contact', 'person', 'driver', 'user', 'parker', 'guest'];
    const possibleEmailHeaders = ['email', 'e-mail', 'mail', 'contact'];
    const possiblePhoneHeaders = ['phone', 'number', 'tel', 'telephone', 'mobile', 'cell', 'contact'];

    const downloadTemplate = () => {
        const headers = ['Plate', 'Name', 'Email', 'Phone'];
        const csvContent = [
            headers.join(','),
            'ABC123,John Doe,john@example.com,555-555-5555'
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'plate_registry_template.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
        const file = event.dataTransfer.files?.[0];
        if (file) {
            processFile(file);
        }
    };

    const processFile = (file: File) => {
        // Reset state before processing new file
        setErrors([]);
        setParsedEntries([]);
        setInfoMessages([]);
        setIsLoading(true);

        let messageIndex = 0;
        const messageInterval = setInterval(() => {
            setLoadingMessage(loadingMessages[messageIndex]);
            messageIndex = (messageIndex + 1) % loadingMessages.length;
        }, 1500);

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = e.target?.result;
                const workbook = read(data, { type: 'binary' });
                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];

                // Try to detect if the file is tab-delimited
                const rawData = utils.sheet_to_txt(sheet, { FS: '\t' });
                const jsonData = utils.sheet_to_json(sheet, { raw: false });

                if (!jsonData || jsonData.length === 0) {
                    throw new Error('Spreadsheet is empty or has no data');
                }

                // Find column indices by header names
                const headers = Object.keys(jsonData[0] || {});
                if (!headers || headers.length === 0) {
                    throw new Error('Could not find column headers in the spreadsheet');
                }

                // Clean and normalize headers
                const normalizedHeaders = headers.map(h => h.trim().toLowerCase());

                const plateIndex = headers.findIndex(h =>
                    possiblePlateHeaders.some(possible => h.toLowerCase().trim().includes(possible.toLowerCase())));
                const nameIndex = headers.findIndex(h =>
                    possibleNameHeaders.some(possible => h.toLowerCase().trim().includes(possible.toLowerCase())));
                const emailIndex = headers.findIndex(h =>
                    possibleEmailHeaders.some(possible => h.toLowerCase().trim().includes(possible.toLowerCase())));
                const phoneIndex = headers.findIndex(h =>
                    possiblePhoneHeaders.some(possible => h.toLowerCase().trim().includes(possible.toLowerCase())));

                if (plateIndex === -1) {
                    throw new Error('Could not find a column for license plate numbers. Please make sure you have a column with "Plate" in the header.');
                }

                if (nameIndex === -1) {
                    throw new Error('Could not find a column for names. Please make sure you have a column with "Name" in the header.');
                }

                // Parse entries
                const entries: ParsedEntry[] = [];
                const newErrors: string[] = [];
                const overlappingPlates: string[] = [];

                jsonData.forEach((row: any, index: number) => {
                    const plate = String(row[headers[plateIndex]] || '').trim().toUpperCase();
                    const name = String(row[headers[nameIndex]] || '').trim();
                    const email = String(row[headers[emailIndex]] || '').trim();
                    const phone = String(row[headers[phoneIndex]] || '').trim();

                    if (!plate || !name) {
                        newErrors.push(`Row ${index + 1}: Missing required fields (plate and name)`);
                        return;
                    }

                    if (email && !/\S+@\S+\.\S+/.test(email)) {
                        newErrors.push(`Row ${index + 1}: Invalid email format`);
                    }

                    if (phone && !/^[\d\s\-()]+$/.test(phone)) {
                        newErrors.push(`Row ${index + 1}: Phone number can only contain numbers, spaces, hyphens, and parentheses`);
                    }

                    // Check for matches with existing entries
                    const existingEntry = existingEntries.find(e => e.plateNumber.toUpperCase() === plate);
                    let matchType: ParsedEntry['matchType'] = 'new';
                    const changes: ParsedEntry['changes'] = {};

                    if (existingEntry) {
                        overlappingPlates.push(plate);
                        if (existingEntry.email === email && existingEntry.phone === phone && existingEntry.name === name) {
                            matchType = 'exact';
                            changes.name = 'unchanged';
                            changes.email = 'unchanged';
                            changes.phone = 'unchanged';
                        } else {
                            // Check name changes
                            if (name !== existingEntry.name) {
                                changes.name = 'changed';
                            } else {
                                changes.name = 'unchanged';
                            }

                            // Check email changes
                            if (!existingEntry.email && email) {
                                changes.email = 'new';
                            } else if (existingEntry.email && email !== existingEntry.email) {
                                changes.email = 'changed';
                            } else {
                                changes.email = 'unchanged';
                            }

                            // Check phone changes
                            if (!existingEntry.phone && phone) {
                                changes.phone = 'new';
                            } else if (existingEntry.phone && phone !== existingEntry.phone) {
                                changes.phone = 'changed';
                            } else {
                                changes.phone = 'unchanged';
                            }

                            // Set matchType based on the most significant change
                            if (changes.name === 'changed') {
                                matchType = 'name-change';
                            } else if (changes.email === 'changed') {
                                matchType = 'email-change';
                            } else if (changes.phone === 'changed') {
                                matchType = 'phone-change';
                            } else if (changes.email === 'new') {
                                matchType = 'email-only';
                            } else if (changes.phone === 'new') {
                                matchType = 'phone-add';
                            } else {
                                matchType = 'plate-only';
                            }
                        }
                    }

                    entries.push({
                        plateNumber: plate,
                        name,
                        email,
                        phone: phone || null,
                        matchType,
                        changes
                    });
                });

                setParsedEntries(entries);
                setErrors(newErrors);

                // Add info message for overlapping plates if any exist
                if (overlappingPlates.length > 0) {
                    if (overlappingPlates.length > 10) {
                        setInfoMessages([`${overlappingPlates.length} plate(s) already exist in your registry.`]);
                    } else {
                        setInfoMessages([
                            `${overlappingPlates.length} plate(s) already exist in your registry: ${overlappingPlates.join(', ')}`
                        ]);
                    }
                }
            } catch (error: any) {
                setErrors([error.message || 'Failed to parse spreadsheet. Please check the format and try again.']);
            } finally {
                clearInterval(messageInterval);
                setIsLoading(false);
            }
        };

        reader.onerror = () => {
            setErrors(['Failed to read the file. Please make sure it\'s a valid CSV file.']);
            setIsLoading(false);
            clearInterval(messageInterval);
        };

        reader.readAsBinaryString(file);
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        processFile(file);
    };

    // Handle drag and drop events
    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        setIsDragging(false);
    };

    const handleDiscard = () => {
        setShowConfirmDiscard(true);
    };

    const confirmDiscard = () => {
        setShowConfirmDiscard(false);
        onClose();
    };

    const handleAddToRegistry = () => {
        // Filter out exact matches and prepare entries for the registry
        const entriesToAdd = parsedEntries.filter(entry => entry.matchType !== 'exact');
        onAddToRegistry(entriesToAdd);
        onClose();
    };

    const handleToggleExactMatches = () => {
        setShowExactMatches(prevState => !prevState);
    };

    if (!isOpen) return null;

    const isUploadView = !isLoading && errors.length === 0 && parsedEntries.length === 0;
    const isPreviewView = !isLoading && errors.length === 0 && parsedEntries.length > 0;
    const isErrorView = !isLoading && errors.length > 0;

    return (
        <div
            className="modal-overlay"
            onDragOver={isUploadView || isErrorView ? handleDragOver : undefined}
            onDragLeave={isUploadView || isErrorView ? handleDragLeave : undefined}
            onDrop={isUploadView || isErrorView ? handleDrop : undefined}
        >
            <div
                className={`upload-spreadsheet-container ${isDragging && (isUploadView || isErrorView) ? 'drag-over' : ''}`}
                style={isPreviewView || isErrorView ? { border: 'none', backgroundColor: 'transparent' } : {}}
            >
                <div className="upload-spreadsheet-modal">
                    <h2>Upload Spreadsheet</h2>

                    {/* Close Button */}
                    <div className="upload-spreadsheet-modal-close" onClick={onClose}>
                        <span className="upload-spreadsheet-modal-close-text">Cancel (ESC)</span>
                        <img src="/assets/x.svg" alt="Close" className="upload-spreadsheet-modal-close-icon" />
                    </div>

                    {isLoading ? (
                        <div className="upload-spreadsheet-loading">
                            <LoadingLogo text={loadingMessage} />
                        </div>
                    ) : errors.length > 0 ? (
                        <div className="upload-spreadsheet-error">
                            <div className="upload-spreadsheet-errors">
                                {errors.map((error, index) => (
                                    <p key={index} className="error">
                                        {error}
                                    </p>
                                ))}
                            </div>
                            <div className="upload-spreadsheet-actions">
                                <button className="button-primary" onClick={() => {
                                    setErrors([]);
                                    setParsedEntries([]);
                                    fileInputRef.current?.click();
                                }}>
                                    Try Again
                                </button>
                                <button className="button-secondary" onClick={downloadTemplate}>Download Template</button>
                                <button className="button-secondary" onClick={onClose}>Cancel</button>
                            </div>
                        </div>
                    ) : parsedEntries.length > 0 ? (
                        <div className="upload-spreadsheet-preview">
                            <p className="preview-note">
                                Found {parsedEntries.length} entries. Review them below before adding to registry.
                            </p>
                            {infoMessages.length > 0 && (
                                <div className="upload-spreadsheet-success">
                                    {infoMessages.map((message, index) => (
                                        <p key={index} className="info-message">
                                            {message}
                                        </p>
                                    ))}
                                </div>
                            )}
                            <div className="preview-table-container table-container">
                                <table className="preview-table app-table">
                                    <thead>
                                        <tr>
                                            <th>Plate</th>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Phone</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {parsedEntries
                                            .sort((a, b) => (a.matchType === 'exact' ? 1 : 0) - (b.matchType === 'exact' ? 1 : 0))
                                            .filter(entry => showExactMatches || entry.matchType !== 'exact')
                                            .map((entry, index) => {
                                                const existing = existingEntries.find(e => e.plateNumber.toUpperCase() === entry.plateNumber.toUpperCase());
                                                return (
                                                    <tr key={index} className={`preview-row ${entry.matchType}`}>
                                                        <td className={existing && existing.plateNumber.toUpperCase() === entry.plateNumber.toUpperCase() ? 'matching-text' : ''}>{entry.plateNumber}</td>
                                                        <td className={
                                                            (existing && existing.name === entry.name ? 'matching-text' : '') +
                                                            (entry.changes?.name === 'changed' ? ' changed-text' : '') +
                                                            (entry.changes?.name === 'new' ? ' new-text' : '')
                                                        }>
                                                            {entry.changes?.name === 'changed' && existing ? (
                                                                <Tooltip
                                                                    text={`Previous: ${existing.name}`}
                                                                    position="right"
                                                                    size="small"
                                                                >
                                                                    {entry.name}
                                                                </Tooltip>
                                                            ) : entry.name}
                                                        </td>
                                                        <td className={
                                                            (existing && existing.email === entry.email ? 'matching-text' : '') +
                                                            (entry.changes?.email === 'changed' ? ' changed-text' : '') +
                                                            (entry.changes?.email === 'new' ? ' new-text' : '')
                                                        }>
                                                            {entry.changes?.email === 'changed' && existing ? (
                                                                <Tooltip
                                                                    text={`Previous: ${existing.email}`}
                                                                    position="right"
                                                                    size="small"
                                                                >
                                                                    {entry.email}
                                                                </Tooltip>
                                                            ) : entry.email}
                                                        </td>
                                                        <td className={
                                                            (existing && existing.phone === entry.phone ? 'matching-text' : '') +
                                                            (entry.changes?.phone === 'changed' ? ' changed-text' : '') +
                                                            (entry.changes?.phone === 'new' ? ' new-text' : '')
                                                        }>
                                                            {entry.changes?.phone === 'changed' && existing ? (
                                                                <Tooltip
                                                                    text={`Previous: ${existing.phone || 'Not provided'}`}
                                                                    position="left"
                                                                    size="small"
                                                                >
                                                                    {entry.phone}
                                                                </Tooltip>
                                                            ) : entry.phone}
                                                        </td>
                                                        <td>
                                                            {
                                                                entry.matchType === 'exact' ? 'Exact Match' :
                                                                    entry.matchType === 'plate-only' ? 'New Info' :
                                                                        entry.matchType === 'new' ? 'New Entry' :
                                                                            (() => {
                                                                                const changes: string[] = [];
                                                                                if (entry.changes?.name === 'changed') changes.push('Replace Name');
                                                                                if (entry.changes?.email === 'new') changes.push('New Email');
                                                                                if (entry.changes?.email === 'changed') changes.push('Replace Email');
                                                                                if (entry.changes?.phone === 'new') changes.push('New Phone');
                                                                                if (entry.changes?.phone === 'changed') changes.push('Replace Phone');
                                                                                const statusClass = changes.some(c => c.includes('Replace')) ? 'status-changed' : 'status-new';
                                                                                return <span className={statusClass}>{changes.length > 1 ? 'Multiple Changes' : changes.join(', ')}</span>;
                                                                            })()
                                                            }
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        {!showExactMatches && parsedEntries.filter(entry => entry.matchType === 'exact').length > 0 && (
                                            <tr className="exact-matches-summary" onClick={handleToggleExactMatches}>
                                                <td colSpan={5}>
                                                    {parsedEntries.filter(entry => entry.matchType === 'exact').length} Exact Match{parsedEntries.filter(entry => entry.matchType === 'exact').length !== 1 ? 'es' : ''} Found <span className="show-text">Show</span>
                                                </td>
                                            </tr>
                                        )}
                                        {showExactMatches && parsedEntries.filter(entry => entry.matchType === 'exact').length > 0 && (
                                            <tr className="exact-matches-summary" onClick={handleToggleExactMatches}>
                                                <td colSpan={5}>
                                                    {parsedEntries.filter(entry => entry.matchType === 'exact').length} Exact Match{parsedEntries.filter(entry => entry.matchType === 'exact').length !== 1 ? 'es' : ''} Found <span className="show-text">Hide</span>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            <div className="upload-spreadsheet-actions">
                                <button className="button-primary" onClick={handleAddToRegistry}>
                                    Add to Registry
                                </button>
                                <button className="button-secondary" onClick={onClose}>Cancel</button>
                            </div>
                            <p className="add-note">
                                Note: Once added these entries may be edited. Please ensure you save registry before leaving page.
                            </p>
                        </div>
                    ) : (
                        <div className="upload-spreadsheet-upload">
                            <div className="upload-text">
                                <img src="/assets/file-spreadsheet.svg" alt="Spreadsheet" />
                                <p>Drag and Drop or Browse File</p>
                            </div>
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileSelect}
                                accept=".csv"
                                style={{ display: 'none' }}
                            />
                            <div className="upload-spreadsheet-actions">
                                <button className="button-primary" onClick={() => fileInputRef.current?.click()}>
                                    Select File
                                </button>
                                <p className="file-types">Support formats: .csv</p>
                                <button className="button-secondary" onClick={downloadTemplate}>Download Template</button>
                                <button className="button-secondary" onClick={onClose}>Cancel</button>
                            </div>
                        </div>
                    )}
                </div>

                {showConfirmDiscard && (
                    <div className="upload-spreadsheet-overlay">
                        <div className="upload-spreadsheet-modal confirm-modal">
                            <h3>Discard Upload?</h3>
                            <p>Are you sure you want to discard these entries? This action cannot be undone.</p>
                            <div className="upload-spreadsheet-actions">
                                <button className="button-secondary" onClick={() => setShowConfirmDiscard(false)}>
                                    Cancel
                                </button>
                                <button className="button-primary" onClick={confirmDiscard}>
                                    Discard
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UploadSpreadsheet; 